import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    styled,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    CircularProgress,
    TextField,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getStatusIcon } from "../../utils/schedule";
import { formatNumberWithCommas } from "../../utils/numberFormatter.js";
import { useTheme } from "@emotion/react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import DatePicker from "../DatePicker.js";
import { useVirtualizer } from "@tanstack/react-virtual";

const buttonExpandStyle = {
    fontSize: "0.75rem!important",
    marginX: 0.5,
    paddingY: 0.3,
    borderRadius: "5px",
};

const StyledHeadTableCell = styled(TableCell)(({ theme }) => ({
    padding: "5.5px 8px",
    fontSize: theme.typography.fontSize,
    whiteSpace: "nowrap",
    color: "#fff",
}));

const datePickerStyle = {
    width: "130px",
    "& .MuiSvgIcon-root": {
        color: "#333",
    },
    "& .MuiIconButton-root": {
        pl: 0,
    },
    "& .MuiInputBase-input": {
        pt: 0.5,
        pb: 0.5,
        fontWeight: 600,
    },
};

// 展平树结构，保留层级和展开状态
function flattenTree(rows, openRows, depth = 0) {
    const result = [];
    for (const row of rows) {
        result.push({ row, depth });
        if (openRows.has(row.taskId) && row.children && row.children.length > 0) {
            result.push(...flattenTree(row.children, openRows, depth + 1));
        }
    }
    return result;
}

const CellContent = React.memo(({ scheduleId, field, value, header, isEditable, children, depth, toggleOpen, isOpen, onRowEdit }) => {
    const [localValue, setLocalValue] = useState(value);
    const handleEditTextChange = (event) => {
        const newValue = event.target.value;
        setLocalValue(newValue);
    };
    const handleEditDateChange = (newValue) => {
        if (!newValue || !dayjs(newValue).isValid()) return;
        const formattedValue = newValue.format();
        setLocalValue(formattedValue);
        onRowEdit(scheduleId, field, formattedValue);
    };
    if (!field) return null;
    switch (header.label) {
        case "Task ID":
        case "Contractor":
        case "Work Type":
            return localValue;
        case "Planned Value":
        case "Earned Value":
        case "Actual Cost":
        case "Additional Actual Cost":
            if (isEditable) {
                return (
                    <TextField
                        disabled={children && children.length > 0}
                        type="number"
                        value={localValue}
                        onChange={handleEditTextChange}
                        variant="outlined"
                        sx={{ "& .MuiInputBase-input": { px: 1, py: 0.2 } }}
                        fullWidth
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => {
                            onRowEdit(scheduleId, field, e.target.value);
                        }}
                    />
                );
            } else {
                return formatNumberWithCommas(localValue);
            }
        case "Task Name":
            return (
                <>
                    {Array.isArray(children) && children.length > 0 ? (
                        <IconButton aria-label="expand row" size="small" onClick={toggleOpen} sx={{ padding: 0, paddingLeft: `${depth * 0.5}rem`, mt: -0.5 }}>
                            {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                    ) : (
                        <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                    )}
                    <Box component="span" sx={{ paddingLeft: children.length ? 0 : `${depth * 0.5}rem` }}>{localValue}</Box>
                </>
            );
        case "": //status
            return getStatusIcon(localValue);
        case "Plan Start":
        case "Plan End":
        case "Actual Start":
        case "Actual End":
            const dateValue = localValue;
            if (isEditable) {
                return (
                    <Box onClick={(e) => e.stopPropagation()}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                disabled={children && children.length > 0}
                                value={dateValue ? dayjs(dateValue) : null}
                                onChange={handleEditDateChange}
                                format="DD/MM/YYYY"
                                renderInput={(params) => <TextField {...params} inputProps={{ ...params.inputProps }} />}
                                sx={{ ...datePickerStyle }}
                                inputFormat="DD/MM/YYYY"
                            />
                        </LocalizationProvider>
                    </Box>
                );
            } else {
                if (dateValue && dayjs(dateValue).isValid()) {
                    return dayjs(dateValue).format("DD/MM/YY");
                }
                return "-";
            }
        default:
            return null;
    }
});

const VirtualTreeTable = React.memo((props) => {
    const theme = useTheme();
    const {
        treeTableHeaders,
        editableColumns,
        onRowEdit,
        rows,
        onRowClick,
        onRadioClick,
        onExpandAll,
        onCollapseAll,
        onToggleOpen,
        showButton,
        onImageClick,
        onScroll,
        ganttscrollTop,
        SimplifiedVersion = false,
        sx,
        defaultExpandAll = false,
        isLoading,
        estimateSize = 24.72,
    } = props;
    const [openRows, setOpenRows] = useState(new Set());
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [radioValue, setRadioValue] = useState("Month");
    const tableContainerRef = useRef(null);
    const [imageSrc, setImageSrc] = useState("assets/icons/arrowdouble.svg");

    const flatRows = useMemo(() => flattenTree(rows || [], openRows), [rows, openRows]);

    const rowVirtualizer = useVirtualizer({
        count: flatRows.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => estimateSize,
        overscan: 10,
    });

    useEffect(() => {
        if (rowVirtualizer) {
            rowVirtualizer.measure();
        }
    }, [estimateSize]);

    const tableHeadStyle = {
        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #008AD8 100%)`,
        position: "sticky",
        top: 0,
        zIndex: 1,
    };

    const expandAll = () => {
        const getAllIds = (row) => {
            const ids = [row.taskId];
            if (row.children && row.children.length > 0) {
                row.children.forEach((childRow) => {
                    ids.push(...getAllIds(childRow));
                });
            }
            return ids;
        };
        const allIds = new Set(rows.flatMap((row) => getAllIds(row)));
        const allIdsArray = Array.from(allIds);
        setOpenRows(allIds);
        onExpandAll && onExpandAll(allIdsArray);
    };

    const collapseAll = () => {
        setOpenRows(new Set());
        onCollapseAll && onCollapseAll([]);
    };

    useEffect(() => {
        if (defaultExpandAll) {
            expandAll();
        }
    }, [defaultExpandAll]);

    useEffect(() => {
        const debounce = (func, wait) => {
            let timeout;
            return function (...args) {
                const context = this;
                clearTimeout(timeout);
                timeout = setTimeout(() => func.apply(context, args), wait);
            };
        };
        const handleScroll = () => {
            if (onScroll && tableContainerRef.current) {
                requestAnimationFrame(() => {
                    onScroll(tableContainerRef.current.scrollTop);
                });
            }
        };
        const container = tableContainerRef.current;
        if (container) {
            container.addEventListener("scroll", debounce(handleScroll, 60));
        }
        return () => {
            if (container) {
                container.removeEventListener("scroll", debounce(handleScroll, 60));
            }
        };
    }, [onScroll]);

    useEffect(() => {
        const syncScroll = (targetScrollTop) => {
            const container = tableContainerRef.current;
            if (container && container.scrollTop !== targetScrollTop) {
                requestAnimationFrame(() => {
                    container.scrollTop = targetScrollTop;
                });
            }
        };
        syncScroll(ganttscrollTop);
    }, [ganttscrollTop]);

    const changeLayout = () => {
        setImageSrc((prevSrc) => (prevSrc === "assets/icons/arrowdouble.svg" ? "assets/icons/arrowdoubleleft.svg" : "assets/icons/arrowdouble.svg"));
        onImageClick && onImageClick();
    };

    const handleRowClick = useCallback(
        (row) => {
            if (selectedRowId === row.taskId) {
                setSelectedRowId();
            } else {
                setSelectedRowId(row.taskId);
            }
            onRowClick?.(row);
        },
        [onRowClick]
    );

    const handleToggleOpen = useCallback(
        (id) => {
            onToggleOpen?.(id);
        },
        [onToggleOpen]
    );

    const handleRadioChange = useCallback(
        (event) => {
            onRadioClick?.(event.target.value);
            setRadioValue(event.target.value);
        },
        [onRadioClick]
    );

    const fieldNameMapping = {
        "Task ID": "taskId",
        "Task Name": "taskName",
        "": "status",
        "Plan Start": "planStart",
        "Plan End": "planEnd",
        "Actual Start": "actualStart",
        "Actual End": "actualEnd",
        Contractor: "contractor",
        "Work Type": "workType",
        "Planned Value": "planValue",
        "Earned Value": "earnValue",
        "Additional Actual Cost": "additionalActualCost",
        "Actual Cost": "actualCost",
    };

    const virtualRows = rowVirtualizer.getVirtualItems();

    return (
        <Box width="100%" height="100%" sx={{ sx }}>
            <Box
                display="flex"
                sx={{
                    height: "35px",
                    lineHeight: "35px",
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #008AD8 100%)`,
                    color: "#fff",
                    pl: 1,
                    justifyContent: "space-between",
                }}
            >
                <Box>
                    <img src="assets/icons/schedule.svg" alt="schedule" style={{ verticalAlign: "sub", marginRight: "5px" }}></img>
                    <Box component="span">Schedule</Box>
                    {props.buttonOnClick && (
                        <IconButton aria-label="container-action" size="small" onClick={props.buttonOnClick} sx={{ p: 0, pl: 1 }}>
                            {props.buttonIcon && <img src={props.buttonIcon} style={{ height: 19 }} alt={"schedule"} />}
                        </IconButton>
                    )}
                </Box>
                {!SimplifiedVersion && (
                    <Box>
                        <FormControl>
                            <RadioGroup
                                sx={{
                                    flexDirection: "row",
                                    color: "#fff",
                                    "& .MuiSvgIcon-root": {
                                        color: "#fff",
                                    },
                                    "& .Mui-checked .MuiSvgIcon-root path": {
                                        fill: "#70E0F1",
                                    },
                                }}
                                aria-label="gender"
                                name="gender1"
                                value={radioValue}
                                onChange={handleRadioChange}
                            >
                                <FormControlLabel value="Month" control={<Radio />} label="month" />
                                <FormControlLabel value="Day" control={<Radio />} label="date" />
                            </RadioGroup>
                        </FormControl>
                        <IconButton onClick={changeLayout} sx={{ width: 30, height: 30 }}>
                            <img src={imageSrc} alt="arrow double"></img>
                        </IconButton>
                    </Box>
                )}
            </Box>
            <TableContainer
                ref={tableContainerRef}
                sx={{
                    maxHeight: "calc(100% - 85px)",
                    minHeight: "calc(100% - 85px)",
                    overflowY: "auto",
                }}
            >
                <Table aria-label="collapsible table">
                    <TableHead sx={tableHeadStyle}>
                        <TableRow>
                            {treeTableHeaders.map((header, index) => (
                                <StyledHeadTableCell key={index} sx={{ width: header.width || "auto" }}>
                                    {header.label}
                                </StyledHeadTableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Box
                                        sx={{
                                            height: 200,
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                        }}
                                    >
                                        <CircularProgress size={theme.typography.fontSize * 3} />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {/* Spacer for items before virtual window */}
                                {virtualRows.length > 0 && virtualRows[0].index > 0 && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={treeTableHeaders.length}
                                            sx={{ height: `${virtualRows[0].start}px`, padding: 0, border: "none" }}
                                        />
                                    </TableRow>
                                )}
                                {/* 虚拟渲染行 */}
                                {virtualRows.map((virtualRow) => {
                                    const { row, depth } = flatRows[virtualRow.index];
                                    const isOpen = openRows.has(row.taskId);
                                    const StyledTableCell = styled(TableCell)(({ theme }) => ({
                                        padding: SimplifiedVersion ? "4.5px 8px" : "4px 8px",
                                        fontSize: theme.typography.fontSize,
                                        whiteSpace: "nowrap",
                                    }));
                                    return (
                                        <TableRow
                                            key={row.taskId}
                                            sx={{ backgroundColor: row.taskId === selectedRowId ? "#E1FEFF" : "transparent" }}
                                            onClick={() => handleRowClick(row)}
                                        >
                                            {treeTableHeaders.map((header, index) => (
                                                <StyledTableCell key={index} component="th" scope="row" sx={{ width: header.width }}>
                                                    <CellContent
                                                        scheduleId={row.scheduleId}
                                                        field={fieldNameMapping[header.label]}
                                                        value={row[fieldNameMapping[header.label]]}
                                                        header={header}
                                                        isEditable={editableColumns.includes(header.label)}
                                                        children={row.children}
                                                        depth={depth}
                                                        toggleOpen={(e) => {
                                                            e.stopPropagation();
                                                            let newOpenRows = new Set(openRows);
                                                            if (isOpen) {
                                                                newOpenRows.delete(row.taskId);
                                                            } else {
                                                                newOpenRows.add(row.taskId);
                                                            }
                                                            setOpenRows(newOpenRows);
                                                            handleToggleOpen(row.taskId);
                                                        }}
                                                        isOpen={isOpen}
                                                        onRowEdit={onRowEdit}
                                                    ></CellContent>
                                                </StyledTableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}
                                {/* Spacer for items after virtual window */}
                                {virtualRows.length > 0 && (() => {
                                    const lastItem = virtualRows[virtualRows.length - 1];
                                    const remainingHeight = rowVirtualizer.getTotalSize() - (lastItem.start + lastItem.size);
                                    return remainingHeight > 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={treeTableHeaders.length}
                                                sx={{ height: `${remainingHeight}px`, padding: 0, border: "none" }}
                                            />
                                        </TableRow>
                                    ) : null;
                                })()}
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box
                sx={{
                    display: "flex",
                    paddingX: 2,
                    paddingY: 1,
                    justifyContent: "space-between",
                    alignItems: "center",
                    height: 50,
                }}
            >
                {!SimplifiedVersion && (
                    <Box display="flex" alignItems="center" flexWrap="wrap" width={360}>
                        <Box>
                            <img src="assets/icons/greencircle.svg" alt="Green Circle" />
                            <Typography variant="span">&nbsp;&nbsp;Complete</Typography>
                        </Box>
                        <Box sx={{ ml: 2 }}>
                            <img src="assets/icons/yellowcircle.svg" alt="Yellow Circle" />
                            <Typography variant="span">&nbsp;&nbsp;WIP</Typography>
                        </Box>
                        <Box sx={{ ml: 7.5 }}>
                            <img src="assets/icons/whitecircle.svg" alt="White Circle" />
                            <Typography variant="span">&nbsp;&nbsp;Not Start</Typography>
                        </Box>
                        <Box sx={{ ml: 11 }}>
                            <img src="assets/icons/orangecircle.svg" alt="Orange Circle" />
                            <Typography variant="span">&nbsp;&nbsp;WIP(Delay)</Typography>
                        </Box>
                        <Box sx={{ ml: 2.6 }}>
                            <img src="assets/icons/redbordercircle.svg" alt="Redborder Circle" />
                            <Typography variant="span">&nbsp;&nbsp;Not Start(Delay)</Typography>
                        </Box>
                    </Box>
                )}
                {showButton && (
                    <Box>
                        <Button
                            onClick={expandAll}
                            variant="outlined"
                            color="inherit"
                            sx={buttonExpandStyle}
                            startIcon={<img src="assets/icons/expand.svg" alt="Expand All" />}
                        >
                            Expand All
                        </Button>
                        <Button
                            onClick={collapseAll}
                            variant="outlined"
                            color="inherit"
                            sx={buttonExpandStyle}
                            startIcon={<img src="assets/icons/minimise.svg" alt="Minimise All" />}
                        >
                            Minimise All
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
});

export default VirtualTreeTable;
